import React, { useMemo } from 'react'

import {
  BasicDataKeys,
  ILWFCCategory,
  ILWFCColumn,
  ILWFCRow,
  LWFCRowData,
} from '~/client/src/shared/components/ListWithFixedColumns/VirtualListHelpers'
import VirtualMultiGrid, {
  IDataRow,
} from '~/client/src/shared/components/ListWithFixedColumns/VirtualMultiGrid'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import { NOOP } from '~/client/src/shared/utils/noop'

import './LogisticsListTable.scss'

interface IProps {
  rows: ILWFCRow[]
  columns: ILWFCColumn[]
  columnKeyToCellRenderer: {
    [columnKey: string]: (value: any) => JSX.Element
  }

  columnKeyToHeaderRenderer?: {
    [columnKey: string]: () => JSX.Element
  }
  columnKeyToCategoryCellRenderer?: {
    [columnKey: string]: (
      rowData: any,
      column: ILWFCColumn,
      columnIndex: number,
      isEdge: boolean,
    ) => JSX.Element
  }
  columnHeaderClassesMap?: {
    [columnKey: string]: string
  }
  columnCellClassesMap?: {
    [columnKey: string]: string
  }
  columnCategoryCellClassesMap?: {
    [columnKey: string]: string
  }
  columnsWidthState?: Map<string, number>
  collapsedCategories?: Map<string, boolean>
  sortedColumnKey?: string
  sortingOrder?: SortOrder
  disabledResizeColumns?: string[]
  fixedColumnsCount?: number

  onColumnSort?: (columnKey: string) => void
  onCellClick?: (rowData: LWFCRowData, columnKey: string) => void
  onCategoryCheckboxToggle?: (category: ILWFCCategory) => void
  onCategoryCollapsingToggle?: (categoryId: string) => void
  selectAll?: () => void
  resetAll?: () => void
}

const DEFAULT_ROW_HEIGHT = 64
const CATEGORY_ROW_HEIGHT = 32

export default function LogisticsListTable({
  rows,
  columns,
  columnKeyToCellRenderer,
  columnKeyToHeaderRenderer,
  columnKeyToCategoryCellRenderer,
  columnHeaderClassesMap,
  columnCellClassesMap,
  columnCategoryCellClassesMap,
  disabledResizeColumns,
  columnsWidthState = new Map(),
  collapsedCategories = new Map(),
  sortedColumnKey,
  sortingOrder = SortOrder.DEFAULT,
  fixedColumnsCount = 1,
  onColumnSort = NOOP,
  onCellClick = NOOP,
  onCategoryCheckboxToggle = NOOP,
  onCategoryCollapsingToggle = NOOP,
  selectAll = NOOP,
  resetAll = NOOP,
}: IProps) {
  // Transform ILWFCRow[] to IDataRow[] format for VirtualMultiGrid
  const transformedRows: IDataRow[] = useMemo(() => {
    return rows.map(row => ({
      data: row.data,
      category: row.category,
      height: row.category ? CATEGORY_ROW_HEIGHT : DEFAULT_ROW_HEIGHT,
      level: row.level || 0,
    }))
  }, [rows])

  // Create unified cellRenderer function
  const cellRenderer = useMemo(() => {
    const columnKeyToDefaultRenderer = {
      [BasicDataKeys.CHECKBOX]: (value: any) => <Checkbox checked={value} />,
    }

    return (columnKey: string, value: any) => {
      // Use custom renderer if provided
      if (columnKeyToCellRenderer && columnKeyToCellRenderer[columnKey]) {
        return columnKeyToCellRenderer[columnKey](value)
      }

      // Use default renderer if available
      if (columnKeyToDefaultRenderer[columnKey]) {
        return columnKeyToDefaultRenderer[columnKey](value)
      }

      // Default fallback
      return value
    }
  }, [columnKeyToCellRenderer])

  // Create unified categoryCellRenderer function
  const categoryCellRenderer = useMemo(() => {
    return (
      rowData: any,
      column: ILWFCColumn,
      columnIndex: number,
      isEdge: boolean,
    ) => {
      // Use custom renderer if provided
      if (!columnKeyToCategoryCellRenderer?.[column.dataKey]) {
        return undefined
      }

      return columnKeyToCategoryCellRenderer[column.dataKey](
        rowData,
        column,
        columnIndex,
        isEdge,
      )
    }
  }, [columnKeyToCategoryCellRenderer])

  // Create unified headerRenderer function
  const headerRenderer = useMemo(() => {
    return (columnKey: string, value: any) => {
      // Use custom renderer if provided
      if (columnKeyToHeaderRenderer?.[columnKey]) {
        return columnKeyToHeaderRenderer[columnKey]()
      }

      // Default fallback
      return value
    }
  }, [columnKeyToCellRenderer])

  return (
    <div className="logistics-list-table relative full-height full-width">
      <VirtualMultiGrid
        rows={transformedRows}
        columns={columns}
        fixedColumnsCount={fixedColumnsCount}
        disabledResizeColumns={disabledResizeColumns}
        columnHeaderClassesMap={columnHeaderClassesMap}
        columnCellClassesMap={columnCellClassesMap}
        columnCategoryCellClassesMap={columnCategoryCellClassesMap}
        cellRenderer={cellRenderer}
        categoryCellRenderer={categoryCellRenderer}
        headerRenderer={headerRenderer}
        onCellClick={onCellClick}
        collapsedCategories={collapsedCategories}
        onCategoryCollapsingToggle={onCategoryCollapsingToggle}
        onColumnSort={onColumnSort}
        sortedColumnKey={sortedColumnKey}
        sortingOrder={sortingOrder}
        columnsWidthState={columnsWidthState}
        selectAll={selectAll}
        resetAll={resetAll}
        onCategoryCheckboxToggle={onCategoryCheckboxToggle}
      />
    </div>
  )
}
